import { AfterViewInit, ChangeDetectorRef, Component, ContentChildren, Directive, EventEmitter, Input, OnInit, Output, QueryList, TemplateRef } from '@angular/core';
import { logUserAction } from '@office-start/telemetry-service';
import { Area } from '@office-start/telemetry-types';

import { ApplicationError, OfficeHomePageErrorHandler } from '../../../error-handler/error-handler.service';
import { ErrorId } from '../../../error-handler/error-id';
import { IconFontType } from '../../icon-font/icon-font-type.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../instrumentation/instrumentation.service';
import { ItemView } from '../../models/item-view.model';
import { TabItem } from '../../models/tab-item.model';
import { TilesListMenuItem } from '../../models/tiles-list-menu-item.model';
import { DeviceDetector } from '../../services/device-detector.service';
import { UserDataStorageService } from '../../services/user-data-storage.service';
import { CapabilitiesService } from '../../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../../shared-context/client-startup-config.service';
import { LocService } from '../../shared-context/loc.service';
import { DropdownMenu } from '../dropdown-menu/dropdown-menu.component';
import { ListState } from '../expandable-list/list-state.enum';
import { MenuSelectItemEvent } from '../menu/menu-select-item.model';
import { TabItemState, TabListModel } from './tab-list.model';

import './tab-list.component.css';

// Keep in sync with TabListState.cs on server
interface TabListState {
    tileView: boolean;
}

@Directive({
    selector: '[ohpTabListItem]'
})
export class TabListItemDirective {
    constructor(
        public templateRef: TemplateRef<any>
    ) { }
}

@Directive({
    selector: '[ohpTabListButton]'
})
export class TabListButtonDirective { }

@Component({
    selector: 'ohp-tab-list',
    templateUrl: './tab-list.component.html'
})
export class TabListComponent implements OnInit, AfterViewInit {

    @ContentChildren(TabListItemDirective) public tabListItems: QueryList<TabListItemDirective>;

    public listItemTemplate: TemplateRef<any>;

    @Input() public model: TabListModel;

    @Input() public columnsEnabled: boolean;

    @Input() public tileViewEnabled: boolean;

    @Input() public leftAlignedZeroExperience: boolean;

    @Input() public hideHeader: boolean;

    @Input() public listState: ListState;

    @Input() public restrictHeight: boolean;

    @Input() public numItems: number;

    @Input() public defaultStateNoMinHeight: boolean;

    @Input() public customButtonEnabled: boolean;

    @Input() public defaultView: ItemView;

    @Input() public flexHeader: boolean;

    @Input() public smallTitles: boolean;

    @Input() public pillTabs: boolean;

    @Input() public showFilter: boolean;

    @Input() public filterInput: (filterText: string) => void;

    @Input() public isVisualRefresh: boolean;

    @Output() public readonly tabClicked: EventEmitter<number> = new EventEmitter<number>();

    public selectedTab: number;
    public showAsListAriaLabel: string;
    public showAsTilesAriaLabel: string;
    public itemView: ItemView;
    public tabAnnouncement: string;
    public tileViewText: string;
    public listViewText: string;
    public toggleAnnouncement: string;
    public tilesListMenuModel: DropdownMenu;
    public tilesListMenuEnabled: boolean;
    public isConsumer: boolean;
    public isTouch: boolean;
    public fluentRefresh: boolean;
    public readonly cardView = ItemView.Card;
    public readonly tileView = ItemView.Tile;
    public readonly listColView = ItemView.ListCol;

    private static readonly TabListStateKey = 'TabListStateKey';

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly deviceDetector: DeviceDetector,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly userDataStorageService: UserDataStorageService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler) {
    }

    public ngOnInit() {
        this.fluentRefresh = this.capabilitiesService.capabilities.fluentRefresh;
        this.tilesListMenuEnabled = !this.deviceDetector.isMobileDevice();
        this.showAsListAriaLabel = this.locService.ListViewToggleAriaLabel;
        this.showAsTilesAriaLabel = this.locService.TileViewToggleAriaLabel;
        this.tileViewText = this.locService.TileViewToggleAriaTooltip;
        this.listViewText = this.locService.ListViewToggleAriaTooltip;
        this.isConsumer = this.startupConfigService.config.authType === AuthType.MSA;
        this.isTouch = this.deviceDetector.isMobileDevice() || this.deviceDetector.isTabletDevice();

        // Run showTab() logic when currently shown tab changes (e.g. as a result of content refresh)
        this.model.registerOnTabAddedOrUpdated((_tabItem: TabItem, index: number, existingTab: boolean) => {
            if (existingTab && this.selectedTab === index) {
                this.showTab(index, true);
            }
        });

        if (typeof this.listState === 'undefined') {
            this.listState = ListState.Closed;
        }

        this.loadTabListState().then(() => {
            if (this.tileViewEnabled && this.tilesListMenuEnabled) {
                this.tilesListMenuModel = this.createTilesListMenuModel();
            }
            this.showTab(this.model.defaultTab || 0, true);
        });
    }

    public ngAfterViewInit() {
        if (this.tabListItems.length !== 1) {
            throw new ApplicationError('Must have exactly 1 *ohpTabListItem in an TabListComponent as a child');
        }

        this.listItemTemplate = this.tabListItems.first.templateRef;
        this.changeDetectorRef.detectChanges();
    }

    public showingTiles() {
        return this.itemView === ItemView.Tile || this.itemView === ItemView.Card;
    }

    public showTimestampHeader() {
        const selectedTab = this.model.tabs[this.selectedTab];
        return !this.columnsEnabled && selectedTab && selectedTab.loaded && !selectedTab.hasError && selectedTab.listModel.items.length && selectedTab.timestampHeader && !this.showingTiles();
    }

    public showTileToggle() {
        const selectedTab = this.model.tabs[this.selectedTab];
        return this.tileViewEnabled && selectedTab && selectedTab.loaded && !selectedTab.hasError && selectedTab.listModel.items.length;
    }

    public selectTab(event: Event, index: number) {
        if (event) {
            event.preventDefault();
        }

        this.showTab(index);
        this.tabClicked.emit(index);
    }

    public toggleTileView() {
        const selectedTab = this.model.tabs[this.selectedTab];
        this.changeTilesListView(this.showingTiles() ? ItemView.List : (selectedTab.showCardsInTileView ? ItemView.Card : ItemView.Tile));
    }

    public itemViewExists() {
        return typeof this.itemView !== 'undefined' && this.itemView !== null;
    }

    public trackByIndex(index: number) {
        return index;
    }

    public onToggleTilesListMenu(_open: boolean): void {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.State] = this.itemView;
        const action = _open ? 'Open' : 'Close';
        params[InstrumentationParam.Action] = action;
        const elementId = `tile-toggle-menu-${this.model.tabs[this.selectedTab].id}`;
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.TileToggleOpen, elementId, this.model.area, null, null, params);
        logUserAction({
            eventName: KnownInstrumentationEvents.TileToggleOpen,
            id: elementId,
            area: this.model.area as Area,
            result: action,
            target: 'TileToggle'
        });
    }

    public onSelectTilesListMenuItem(event: MenuSelectItemEvent): void {
        const item: TilesListMenuItem = event.item as TilesListMenuItem;
        let itemView = item.view;
        if (itemView === ItemView.Tile) {
            const selectedTab = this.model.tabs[this.selectedTab];
            if (selectedTab.showCardsInTileView) {
                itemView = ItemView.Card;
            }
        }
        if (this.tilesListMenuModel) {
            this.tilesListMenuModel.linkAriaLabel = item.view === ItemView.List ? this.locService.ListViewMenuAriaLabel : this.locService.TileViewMenuAriaLabel;
        }
        this.changeTilesListView(itemView);
    }

    public widgetAttributionClick(event): void {
        if (this.model.tabs[this.selectedTab].attribution.onClick) {
            event.preventDefault();
            this.model.tabs[this.selectedTab].attribution.onClick();
        }
    }

    private showTab(index: number, suppressAnnouncement = false) {
        if (!this.model.tabs || !this.model.tabs.length) {
            return;
        }
        this.tabAnnouncement = '';
        this.selectedTab = index;
        const newTab = this.model.tabs[index];
        if (this.showingTiles()) {
            this.itemView = newTab.showCardsInTileView ? ItemView.Card : ItemView.Tile;
        }
        this.ensureTabLoaded(newTab).then(loadingShown => {
            const shownTab = this.model.tabs[index];
            const numItems = shownTab.hasError ? -1 : shownTab.listModel.itemsShown(this.listState === ListState.Open, this.showingTiles());
            const items = shownTab.hasError ? [] : shownTab.listModel.items;
            if (loadingShown) {
                this.model.logImpression(items, shownTab.id, numItems, this.itemView, shownTab.fromCache, TabItemState.Loading);
            }

            if (this.selectedTab === index) {
                this.model.logImpression(items, shownTab.id, numItems, this.itemView, shownTab.fromCache, shownTab.hasError ? TabItemState.Error : TabItemState.Loaded);
                if (!suppressAnnouncement) {
                    // Extra periods added for pauses in case they are not already appended to the titles
                    // Timeout allows announcement to clear so that separate tabs with same # of items are still announced
                    setTimeout(() => this.tabAnnouncement = shownTab.hasError ?
                        (shownTab.errorTitle ?
                            `${shownTab.errorTitle}. ${shownTab.errorSubtitle || ''}`
                            : '')
                        : (numItems ?
                            (this.model.tabActivationAriaLabel ?
                                this.locService.format(this.model.tabActivationAriaLabel, shownTab.titleForAnnouncement || shownTab.title, this.locService.localizedNumberString(numItems))
                                : '')
                            : (shownTab.zeroExperienceTitle ?
                                `${shownTab.zeroExperienceTitle}. ${shownTab.zeroExperienceSubtitle || ''}`
                                : ''
                            )
                        )
                        , 0);
                }
            }
        });
    }

    private changeTilesListView(itemView: ItemView) {
        if (this.itemView !== itemView) {
            const showingTiles = this.showingTiles();
            this.itemView = itemView;
            this.toggleAnnouncement = showingTiles ? this.locService.ListViewToggleUpdateAriaLabel : this.locService.TileViewToggleUpdateAriaLabel;
            this.saveTabListState();
            const params: InstrumentationParams = {};
            params[InstrumentationParam.State] = itemView;
            const elementId = `tile-toggle-${this.model.tabs[this.selectedTab].id}`;
            this.instrumentationService.logClickEvent(KnownInstrumentationEvents.TileToggleSelect, elementId, this.model.area, null, null, params);
            logUserAction({
                eventName: KnownInstrumentationEvents.TileToggleSelect,
                id: `${elementId}-${itemView}`,
                area: this.model.area as Area,
                result: 'Select',
                target: 'TileToggle'
            });
        }
    }

    private saveTabListState() {
        const currentState: TabListState = {
            tileView: this.showingTiles()
        };
        this.userDataStorageService.set<TabListState>(this.tabListStateKey(), currentState);
    }

    private loadTabListState(): Promise<void> {
        return this.userDataStorageService
            .get<TabListState>(this.tabListStateKey(), 0)
            .then(roamingTabState => {
                if (!roamingTabState) {
                    this.itemView = (this.tileViewEnabled && this.defaultView) || (this.defaultView === ItemView.ListCol) ? this.defaultView : ItemView.List;
                    return;
                }

                const cardViewEnabled = this.model && this.model.tabs && this.model.tabs.length && this.model.tabs[this.model.defaultTab || 0].showCardsInTileView;
                this.itemView = roamingTabState.tileView ? (cardViewEnabled ? ItemView.Card : ItemView.Tile) : (this.defaultView === ItemView.ListCol) ? ItemView.ListCol : ItemView.List;
            })
            .catch(error => {
                // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                this.globalErrorHandler.handleError(error, ErrorId.I6SDK);

                this.itemView = (this.tileViewEnabled && this.defaultView) || (this.defaultView === ItemView.ListCol) ? this.defaultView : ItemView.List;
            });
    }

    private tabListStateKey(): string {
        return `${TabListComponent.TabListStateKey}_${this.model.id}`;
    }

    private ensureTabLoaded(tab: TabItem, suppressAnnouncement: boolean = false): Promise<boolean> {
        if (tab.loaded) {
            return Promise.resolve(false);
        }
        if (!tab.onLoad) {
            tab.loaded = true;
            return Promise.resolve(false);
        }
        if (!suppressAnnouncement) {
            this.tabAnnouncement = this.locService.TabLoadingAriaLabel;
        }
        return tab.onLoad()
            .catch(() => tab.hasError = true)
            .then(() => {
                tab.loaded = true;
                if (!tab.listModel) {
                    tab.hasError = true;
                }
                return true;
            });
    }

    private createTilesListMenuModel(): DropdownMenu {
        const menuItems: Array<TilesListMenuItem> = [
            {
                title: this.listViewText,
                view: ItemView.List,
                icon: IconFontType.ListBullets,
                ariaLabel: this.locService.ListViewMenuItemAriaLabel,
                ariaSelected: this.itemView === ItemView.List,
                getId: () => 'list'
            },
            {
                title: this.tileViewText,
                view: ItemView.Tile,
                icon: IconFontType.TileView,
                ariaLabel: this.locService.TileViewMenuItemAriaLabel,
                ariaSelected: this.itemView === ItemView.Tile,
                getId: () => 'tile'
            }
        ];
        return {
            id: 'tiles-list-menu',
            items: menuItems,
            linkAriaLabel: this.showingTiles() ? this.locService.TileViewMenuAriaLabel : this.locService.ListViewMenuAriaLabel,
            linkCssClass: 'icon-button tab-list-header__spacer ms-fcl-nd-h ms-fcl-ns ms-bgc-g40-h',
            linkTooltip: this.locService.ListTileMenuAriaTooltip,
            rightAligned: true,
            hostComponentArea: this.model.area,
            announceItemIsSelected: true
        };
    }
}
